import { eHoitoEvent } from '@mehilainen/omamehilainen-types/lib/ehoito'
import { Dayjs } from 'dayjs'
import equal from 'fast-deep-equal'
import {
  ActorRefFrom,
  assign,
  fromPromise,
  sendParent,
  setup,
  assertEvent,
  enqueueActions,
  and,
} from 'xstate'

import {
  AppointmentType,
  NodeAutocompleteResult,
  PractitionerDetails,
  PublicNode,
  SupportedLanguage,
} from '../__generated__/api'
import {
  AppointmentDuration,
  AppointmentLengthOption,
} from '../common/components/AppointmentLengthSelect/AppointmentLengthSelect'
import { GroupedFlexibleScheduleOption } from '../common/components/AppointmentLengthSelect/FlexibleScheduleAppointmentLengthSelect'
import { FilterOptions } from '../common/components/FilterOptions/types'
import { SearchTreeItem } from '../common/components/Search/types'
import i18n from '../common/i18n/i18n'
import authenticationService from '../common/services/authenticationService'
import ohcService from '../common/services/ohc'
import { groupFlexibleScheduleOptions } from '../domain/search/utils'
import {
  allLocationsSelection,
  defaultDentalSearchNodeId,
  defaultOhcSearchNodeId,
  defaultSelectedDate,
  defaultSelectedSearchNodeId,
  defaultSelectedStandardScheduleOption,
  getDefaultSelectedLocation,
  setDefaultSelectedLocation,
} from '../state/search/atoms'

import {
  fetchNode,
  fetchOMEvents,
  fetchPractitionerDetails,
  parseURL,
  resolveServiceSelection,
} from './actors'
import {
  defaultFilterOptions,
  isPublicNode,
  ParsedURL,
  ResolvedService,
  SearchTarget,
  SearchTargetValue,
  SelectedNode,
  SelectedPractitioner,
} from './types'

export const urlMachine = setup({
  types: {
    context: {} as {
      parsedURL?: ParsedURL
    },
    events: {} as
      | {
          type: 'notifyURLParsed'
        }
      | { type: 'xstate.done.actor.parseURL'; output: ParsedURL }
      | { type: 'setSearchParams'; urlSearchParams: URLSearchParams },
  },
  actions: {
    notifyURLParsed: sendParent(({ context }) => ({
      type: 'urlParsed',
      value: context.parsedURL!,
    })),
    setURLContext: assign(({ event }) => {
      assertEvent(event, 'xstate.done.actor.parseURL')
      return { parsedURL: event.output }
    }),
    setSearchParams: ({ event }) => {
      assertEvent(event, 'setSearchParams')
      const url = new URL(window.location.href)
      url.search = event.urlSearchParams.toString()
      window.history.replaceState(null, document.title, url)
    },
  },
  actors: {
    parseURL,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFUBKAZACgQwE6zFwDoBJAOwEsAXC7AGwGIIB7MsIisgN2YGt2ADngJp0AbQAMAXUSgBzWNQqtZIAB6IAjJqKaJAVgDMAJkMA2ACwmLE8wA4ANCACeW43aKWAnAHZjNi3cLLy87AF8IpzJmCDhVURx8QlV5RRoVJHVEAFpDfS8iYy99PW19C30fCwsfJ1cEQw87HzN3M3KgkPCwpwThQlJKGnoUhSUM0A0EXO1C4tLNcsrq2pc3MyJDFraOu2DQyJA+pOJjgghRtOUyVSnjEqIrH00zQy9AxffDOvXNlsNDJp3HtNM1uhEgA */
  context: {},
  id: 'URLParser',
  initial: 'Initial',
  states: {
    Initial: {
      invoke: {
        id: 'parseURL',
        input: {},
        onDone: {
          target: 'URLParsed',
          actions: {
            type: 'setURLContext',
          },
        },
        src: 'parseURL',
      },
    },
    URLParsed: {
      on: {
        setSearchParams: {
          target: 'URLParsed',
          actions: {
            type: 'setSearchParams',
          },
        },
      },
      entry: {
        type: 'notifyURLParsed',
      },
    },
  },
})

export const modalMachine = setup({
  types: {
    context: {} as {
      searchBreadcrumb: PublicNode[]
      parsedHistory: ParsedURL
      selectedNodeId: string
      selectedPractitionerId: number | undefined
      selectedPractitioner: PractitionerDetails | undefined
      selectedStandardScheduleOption: AppointmentDuration | undefined
      preselectedStandardScheduleOption?: AppointmentDuration
      selectedFlexibleScheduleOption: GroupedFlexibleScheduleOption | undefined
      modalsResolved: boolean
      omEvents?: eHoitoEvent[]
      selectedOMCarePlanGuid?: string
      replaceServiceModalWithNoteModal?: boolean
      selectedNoteModalId?: string
    },
    events: {} as
      | { type: 'login' }
      | { type: 'closeModal' }
      | { type: 'discardNoteModal' }
      | {
          type: 'selectService'
          value: Array<SearchTreeItem<PublicNode>> | PublicNode | NodeAutocompleteResult
        }
      | { type: 'openServiceModal' }
      | { type: 'openPractitionerModal' }
      | { type: 'openFilterModal' }
      | { type: 'openDateModal' }
      | { type: 'openLocationModal' }
      | { type: 'openCallbackModal' }
      | { type: 'openInsuranceModal' }
      | {
          type: 'selectPractitionerOption'
          value: AppointmentLengthOption | GroupedFlexibleScheduleOption | undefined
        }
      | { type: 'displayAllServices' }
      | { type: 'displayFavoriteServices' }
      | { type: 'displayServicesDefaultView' }
      | { type: 'searchCallbackAppointments' }
      | { type: 'searchTargetChanged'; value: SearchTarget }
      | { type: 'urlParsed'; value: ParsedURL }
      | { type: 'openAuthenticationModal' }
      | { type: 'openOMLightModal'; carePlanGuid: string }
      | { type: 'selectCarePlanGuid'; carePlanGuid: string | undefined }
      | { type: 'xstate.done.actor.fetchPractitionerDetails'; output: PractitionerDetails }
      | { type: 'xstate.done.actor.fetchNode'; output: PublicNode | undefined }
      | {
          type: 'xstate.done.actor.resolveServiceSelection'
          output: ResolvedService
        }
      | { type: 'xstate.done.actor.fetchOMEvents'; output: eHoitoEvent[] },
  },
  actions: {
    notifyNodeSelected: sendParent(({ context }) => ({
      type: 'nodeSelected',
      value: { nodeId: context.selectedNodeId },
    })),
    notifyPractitionerSelected: sendParent(({ context }) => ({
      type: 'practitionerSelected',
      value: {
        practitionerId: context.selectedPractitioner?.id,
        option: context.selectedFlexibleScheduleOption ?? context.selectedStandardScheduleOption,
      },
    })),
    searchTargetChanged: assign(({ event }) => {
      assertEvent(event, 'searchTargetChanged')
      const searchTarget = event.value
      const common = {
        selectedPractitionerId: undefined,
        selectedPractitioner: undefined,
        selectedFlexibleScheduleOption: undefined,
        selectedStandardScheduleOption: undefined,
      }
      if (searchTarget.value === SearchTargetValue.Node) {
        return {
          ...common,
          selectedNodeId: searchTarget.id,
        }
      }
      if (searchTarget.value === SearchTargetValue.Practitioner) {
        return {
          ...common,
          selectedPractitionerId: searchTarget.id,
        }
      }
      return {}
    }),
    setContextFromHistory: assign(({ event }) => {
      assertEvent(event, 'urlParsed')
      return {
        lang: i18n.language as SupportedLanguage,
        ...(event.value.nodeId
          ? { selectedNodeId: event.value.nodeId }
          : event.value.isOHC
          ? { selectedNodeId: defaultOhcSearchNodeId }
          : event.value.isDental
          ? { selectedNodeId: defaultDentalSearchNodeId }
          : {}),
        ...(event.value.practitionerId
          ? { selectedPractitionerId: event.value.practitionerId }
          : {}),
        parsedHistory: event.value,
        ...(event.value.parentNode ? { searchBreadcrumb: [event.value.parentNode] } : {}),
        ...(event.value.noteModal ? { selectedNoteModalId: event.value.noteModal } : {}),
        ...(event.value.modalAction === 'replace_service_modal'
          ? { replaceServiceModalWithNoteModal: true }
          : {}),
        ...(event.value.preselectedStandardScheduleOption
          ? { preselectedStandardScheduleOption: event.value.preselectedStandardScheduleOption }
          : {}),
      }
    }),
    setSelectedNode: assign({
      selectedNodeId: ({ event }) => {
        assertEvent(event, 'xstate.done.actor.resolveServiceSelection')
        return event.output?.id?.toString() ?? ''
      },
      selectedPractitionerId: undefined,
      selectedPractitioner: undefined,
      selectedFlexibleScheduleOption: undefined,
      selectedStandardScheduleOption: undefined,
    }),
    setBreadcrumb: assign({
      searchBreadcrumb: ({ event }) => {
        assertEvent(event, [
          'xstate.done.actor.resolveServiceSelection',
          'xstate.done.actor.fetchNode',
        ])
        if (isPublicNode(event.output)) {
          return [event.output]
        }
        return []
      },
    }),
    setEmptyBreadcrumb: assign({
      searchBreadcrumb: [],
    }),
    notifyPractitionerIdSelected: sendParent(({ event }) => {
      assertEvent(event, 'xstate.done.actor.resolveServiceSelection')
      return {
        type: 'practitionerSelected',
        value: {
          practitionerId: event.output?.id,
        },
      }
    }),
    setSelectedPractitioner: assign({
      selectedPractitioner: ({ event }) => {
        assertEvent(event, 'xstate.done.actor.fetchPractitionerDetails')
        return event.output
      },
    }),
    setSelectedPractitionerOption: assign(({ event }) => {
      assertEvent(event, 'selectPractitionerOption')
      const option = event.value

      if (!option) {
        return {}
      }

      if ('availableDurations' in option) {
        return {
          selectedFlexibleScheduleOption: option,
        }
      }

      return {
        selectedStandardScheduleOption: option.duration,
      }
    }),
    autoselectPractitionerOption: assign(({ context }) => {
      const practitioner = context.selectedPractitioner

      if (!practitioner) {
        return {}
      }

      if (practitioner.hasFlexibleSchedule && practitioner.flexibleScheduleOptions.length === 1) {
        return {
          selectedFlexibleScheduleOption: groupFlexibleScheduleOptions(
            practitioner,
            i18n.language
          )[0],
        }
      }

      if (context.preselectedStandardScheduleOption) {
        return {
          selectedStandardScheduleOption: context.preselectedStandardScheduleOption,
          preselectedStandardScheduleOption: undefined,
        }
      }

      if (
        practitioner.assisDentEmployeeId &&
        practitioner.assisDentReasons?.length &&
        practitioner.assisDentReasons?.length > 0
      ) {
        return {
          selectedStandardScheduleOption:
            `reason-${practitioner.assisDentReasons[0].id}` as AppointmentDuration,
        }
      }

      return {
        selectedStandardScheduleOption:
          defaultSelectedStandardScheduleOption as AppointmentDuration,
      }
    }),
    setModalsResolved: assign({ modalsResolved: true }),
    setOMEventsToContext: assign({
      omEvents: ({ event }) => {
        assertEvent(event, 'xstate.done.actor.fetchOMEvents')
        return event.output
      },
    }),
    setEmptyOMEventsToContext: assign({
      omEvents: undefined,
    }),
  },
  actors: {
    fetchNode,
    fetchOMEvents,
    resolveServiceSelection,
    fetchPractitionerDetails,
  },
  guards: {
    isOHCAndNotLoggedIn: ({ context }) => {
      return context.parsedHistory.isOHC && authenticationService.getLoginStatus() === undefined
    },
    shouldSkipInitialModals: ({ context }) => {
      return (
        context.parsedHistory.pathname !== '/' && context.parsedHistory.pathname !== '/tyoterveys'
      )
    },
    shouldDisplayLandingModal: ({ context }) => {
      return (
        !context.parsedHistory.isDental &&
        !context.parsedHistory.isOHC &&
        !context.parsedHistory.noLanding &&
        !authenticationService.getIsPartner() &&
        !sessionStorage.getItem('landingModalShown')
      )
    },
    shouldDisplayServiceModal: ({ context }) => {
      return (
        (((context.parsedHistory.node?.children?.length ?? 0) > 0 ||
          context.parsedHistory.isDental ||
          context.parsedHistory.isOHC) &&
          !context.parsedHistory.noServiceSelect) ||
        Boolean(context.parsedHistory.openServiceSelect)
      )
    },
    hasNodeSelectedWithNoChildren: ({ context }) => {
      return (
        Boolean(context.parsedHistory.node) && context.parsedHistory.node?.children?.length === 0
      )
    },
    shouldDisplayPractitionerModal: ({ context }) => {
      return Boolean(context.parsedHistory.practitionerId)
    },
    shouldDisplayCallbackModal: ({ context }) => {
      return Boolean(context.parsedHistory.callbackOpen)
    },
    fetchNodeNotFound: ({ event }) => {
      assertEvent(event, 'xstate.done.actor.fetchNode')
      return !event.output
    },
    fetchedNodeHasChildren: ({ event }) => {
      assertEvent(event, 'xstate.done.actor.fetchNode')
      return (event.output?.children ?? []).length > 0
    },
    selectedServiceIsUndefined: ({ event }) => {
      assertEvent(event, 'xstate.done.actor.resolveServiceSelection')
      return typeof event.output === 'undefined'
    },
    selectedServiceIsNodeWithChildren: ({ event }) => {
      assertEvent(event, 'xstate.done.actor.resolveServiceSelection')
      return event.output !== undefined && ((event.output as PublicNode)?.children?.length ?? 0) > 0
    },
    selectedServiceHasNoteModal: () => {
      // TODO
      return false
    },
    selectedServiceIsPractitioner: ({ event }) => {
      assertEvent(event, 'xstate.done.actor.resolveServiceSelection')
      return event.output?.type === 'practitioner'
    },
    practitionerHasOptions: ({ context, event }) => {
      assertEvent(event, 'xstate.done.actor.fetchPractitionerDetails')
      const practitioner = event.output

      if (practitioner.hasFlexibleSchedule && practitioner.flexibleScheduleOptions.length > 1) {
        return true
      }

      if (
        practitioner.assisDentEmployeeId &&
        practitioner.assisDentReasons?.length &&
        practitioner.assisDentReasons.length > 1
      ) {
        return true
      }

      if (
        practitioner.appointmentLengthOptions.length > 0 ||
        practitioner.bookingInstructions ||
        context.selectedStandardScheduleOption === 'callback'
      ) {
        return true
      }

      return false
    },
    hasSelectedPractitioner: ({ context }) => {
      return Boolean(context.selectedPractitionerId)
    },
    practitionerOptionSelectionFulfilled: ({ context }) => {
      return (
        Boolean(context.selectedStandardScheduleOption) ||
        Boolean(context.selectedFlexibleScheduleOption)
      )
    },
    shouldDisplayInsuranceModal: ({ context }) => {
      return context.parsedHistory.hasInsurance
    },
    searchTargetChangedToPractitioner: ({ event }) => {
      assertEvent(event, 'searchTargetChanged')
      return event.value.value === SearchTargetValue.Practitioner
    },
    isModalsUnresolved: ({ context }) => {
      return !context.modalsResolved
    },
    shouldSkipOMLightModal: ({ context }) => {
      return !context.omEvents || context.omEvents.length === 0
    },
    noteModalReplacesServiceModal: ({ context }) => {
      return Boolean(context.replaceServiceModalWithNoteModal)
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFkD2ECGAbWBiWYGATgMYAWAKsTAC4DCZGAdjBANoAMAuoqAA6pYASxpDUTXiACeiALQBGAOwBWADQgAHogAsygBwBOAHQHdB03qW7tAXxvq0mHPkKlK1MPUYtIbeTyQQAWFRcUkZBFkAJgA2KKM1TURlKKj5EwMovRiDS0VrOwd0bFgjAEkmESFsXABXIiwABWICdm5JYKqwwK1IgGY+ow4DeTSo80V5PT7lRXUI2RijWMztRUmVeQ4cvUKQRxKjAAkhWBpUIikAJThzokhcTgD+QS6JHsRUvWWpxSjFXLKJQxabzRBbbRGSzKGJ9GIqTL-PS2ez7Yo4Y6nO7XW4XB5+Z5BV6hd6gXqxJZ9KIcf76KkGAFRMEIKYcBJbeSzbQGPraWIoopOUoAQVqNDIYCYohIGBJBywuCwqCgQiYTw6xLEpKSLOUHGUywBylmfT0HDNHD6zPksJMUT5igGa2RBmNe3lpRusFQWAAbqqoPLHu1Ap0SZJyTbDbMOEoONoQeNtMycoo7TCaSC4aaBWihUYvT7-SwgwSNSEtRHwXp4nztJy4jbG6brYozQlTONzLC+rl3ejPXAiwHS1FCWHKx8WXo0ypUjF5Aya+tTMzqTMhmsgTFtrMGf384W-SP0Y8+uPNd0yeCRstzNSYro-lllMnpJ9TQb-kpF0o29yD0OI9i0DU82G0C8KyvHVF0GKZcnketHxyOI1zWSF8g4eNv0tRDAIxYCTycR5lEgt4qwQWJ0mUAwd2UGYgXyJl30oxR4yhEEcgMc1tAGeR8MHb1jxLMCYjI8MpxSb5LUMGdF20M17WtWI03GGcsi3PQ6QEowABlmAgIialQPhJTobAsAAIwwEgAGt5XVUNL21XpZATeIYhiGisN7IFXzmFiolNQYaRtQwxj7VEPT0gyjIVEglQIByQxeKCXLkWFvkdHYtMtHceTXNiPKmTyRnkuIdP0phDJE4iTMlCpYHqZgSDAZLxMna9IgTNMtl5UZ5AGNJDDXS14gBfJtG2UZHRnHSAGUwCIf1WqDBLBDa9FHNS8jJK8ox0MfbINOGeRrT6Tk73U5EVC06YFqWlbNqccpKlEGoIHEMAjFVX1UFs76ADNPHIAA5dAwG2ok0oowagQSbQFIGfUVF7ZlDHSR94yYxQd17KIHuWoRVvRV6qg+r6fqYP6AaMYGaDBiGyycmGp0G10jFNLSbVGV8pjfCItj1H4Rm4hcE3kQbCae+UyfehVPqYb7fv+oGQbIcGIEhsdy12rrBu2IwlEMEZsvMAZ0d4owsfQ-48cyaXiee7AjAAEVOPgsAwKRNbABghCwCB7iYFwsDAEgaEWonWqhidoPJOJIVjTCF31fR-mZbj0lyTJ9S0lJTsdkmXvd2BPe933-cD4PcEMsuvakKOntgV2wEBjBaiwGgADUhDAAB3WPnIo6kpoSSwaSidP4OYwWaQNe8F0Q2FJdMIvnawN2247rve4H0Pw8jx6naH1murUtkYXtLZ4RnUxEkFyXBiC6YbT6DgJrXqKByMJundl1u7dO49z7v3IwAAxDAf0iAiDAH-VqeA67lykMKLAWB4FwFPnrGCV8Ox6GyIjLCWE1jnWpNbXQWRUgOl5OvAB29gF7zAag9Bx8EG1w9g3SB0DYEYNgFgiS58fIJCyOFcKNFSFskfFJKh-waHf3zBg2WhEWAYMWmHCOWpa6UxVrTe4QlfRwNYYY9RJJ+GdR1LyfURtuKmFNLEc06xlKXXMDCA2uNXQwloaTZRUBVFgBMZoxWytqaqyMHooshjo7GMPlqZmO0BEWJmMYc0BcLq9kTNaZ0RhHT6BUPoUwWwCbyMOIo7xQ5hK+KMWomJ4gtFKypjTb64S-SRKetUjR4g2A6xZtg3oAxHQJG2FpWE2xnSthSCYXicJ1jjGGJ44pGJSkvR8X4gJtSgkNNCc0gxqyalqnPLrBJfS+gAiGPRAEZp+pZAChENIhtPK9iQopN0CzShLJdisqp-i9l1OCY0sJ5SdlfLWWqCChzzF9N0OkOEdF7T4Nxo6Ncnlvg2lmNMS0cJ1w6UaEQGyogSRLVlhUcmCttEhNpvTcgOK8VvCWq3GgGAA58JStDXpN59DWxNvGHkvFhizxvLaKaM54RmHNPxV5RhqUaIJUQIlb1qikvqTotWDMyBSvxVqOlnhGU4Diayo5nxYzJN5cimarozqBTmQdUwmRcgAibNi3F0rNWytJqXZB6raVEAAPJ8BJHgAgJjPUyt9aYllcd0qUW5IMfBIJkQ0l4rjVsyIhiwnyB418XldgSuDS6gBHDva5q+j6v1Wo8DrSSltcNw8pzXMGAiWMq9uZItOeFL4+RXzjEdTSmVstdKoAwDVKARalZEHpTqxBZL-mUrVU6jVxbx1MrMfHT43JIS-m5OneEqQ1w2iTiCBMsl8kGB0uDIM9UmAjsJVWjqK6o1BQSFSGEXlLQrgFp8Iq1spjTGhGK3M0Uz2ngve8rAy7I1CyypkJsfIeQDUKjSL92RX5BU-Ke1A57TJMFdrKDeYHYZsTTNhXGSMaIgmZBdeEhpciIzUojZQaGMOSnAQHGg16nB4drXqWcfIaTTE3ajcjVgDoTTpJkSYNoGNAcw-2mUcob3grvb+aiQJrAnPfroQTlGshEKUDay5km6qYdFOKSU0pZRanagpyNblbR20sOFbktr32RCCksD+MJEaTCnoNPoBnjKYe9cgXSQgoBkBoJZnpBruqSyhOJv4Jy2KKWZAoRGj6gp+XjKmGYOlzJoOsnZNaiVcPVrPjqBQt57T6EGjWHIpobkfoQwufBwyRjTEUDliy+X7KngIMQcguWrI2VssKPgAhVQ0AALameZbe6zsQDReQfLyV0bY2xrinqpXI78axTRNPRiVvtCsbQi-EiFOh37xA-sMCwVhXzweKtzHkDJHS+YlY1ZqTBi41ArcV2bsMDQ0m5cibInksIWtuZ+prfwpkve2DpbDrGjuVvYyVtlupKMzF5AmGiOcDCadUhcmj1G6M6WY13Nj32isnf1WdyimO7SLh-esXQMRBP5GEwyUT35xWCkODJ8z4gke-asyPOGywKQf3wRLfHywLTJz05aUn6sAyBYAKIGKlJOpV5KVXkDVxrmgM2Rds3ggkLyxGch6ipMyKaym5xI3WJipXqqVfIHV9N3AS0iAXCMOXGggMLgTbpurfX02OP6x-MsawkswoXWNDb8wB0gQ1hmH1XG+3ecYkC8F0L4XTw-epxGii9rraTHc2aXiSgH6IHfksIEeda88txjEHS2eQthdLKjqL6w2QySnkthc+g1wwiGNxfQy58HbCnjpVXGgqgsGMxKKUxMBch3DxY20wOaxYR5HbIKNv6wJDbLGHIfIawjBn3P0QC+xRL7MySR4-hjddVkNkNk782xTx-VNRG5H6zGDE03Q83flfB0kX1MxXzk2IgL3k0i1pxfSGDsQLlhEHz0GtH2l3FxkMBhGyEyH21RCYAhngECA9GfzK24m+A-xrDpHjV-xYlkGWwyBcUyBqyoR0mJXljINclSAAPOXohGFiHtCnnQKTxe2mSmjSH0H-R-hODOAuBxDkPuAgC4M+B3CGD+C5C8lhAzhYijCfBOX+FjBnA-zANvwgNkws3RBUJZERiyifXMG4jWHyGtAQxhDcKniXlSCCh0h8XlGsPsQOh3GOnkkUmc1r3ZH1E3UMN4i0kqlilqmwGsMWATAOljBkl7HwUKTQj+GtTB3+GsF0Fe0zzeSMT8LgLvXwQWx7E8jNh5CtBYhUCWEQn1EQnNieS8Reg4IVWsLqOtmqPhDhHNnqIiEMEhDtR31tWpCKWKN-lKLdQLR9ghiriDklH8IunXSGXNEtAUgIUzljBMAxmNDSHfloh5zzBKTmJLnoV3lAWsM5EQjwQISmiIRIV0N0yNhoikONBBABA6JdkAR3hAQHggSgQuB4SMWINO0UzbG+GpA-lqxomFWc1GE8mtk+JiN8hPz+M3gBIYVASMGYV4TuLNEoJpBmCwntEyFQl0N7EGDiCxnTkmgGGxILEBQDF2Q6W1CL1rVwKGH1Cnh5EBy0nRi0gOlwIZAu2h2kPzCvVdScDuL5EGEYksEVL1D3XQNojNxmC8P5it27WdWLTlRJQVMmHZDbEQnSxP2RIsAOl4lFgZGdC-hmNlPzXrkLTnS9VDTLX8LXVHzSKCgmi8mrxZCXE5lyBnFOg0hOX1PnVHT7QHSHVlMXRwB9LIQBDSQELomc3nG+F4jiBoj5C+CKPOIxEA3lPKMjTSEP2GHWFmEGgxPI3c2yRxxP3+H5A6zyyGzKKhLm32P+DYixnhTNHBw-GmCGDSBBCg3GAmjQy1m7JpzvXrHbBun1B5HTjhHGXSERnpB7ywhBDOOine1xU+w3iSKjDiBhT3VxkXDbNGipGyQdKmgXFkTmglQR1PIrP+zSAOmpDnGe10GDL4molRQunWFkmZIlTJ1YzlMSM-M42NE3GhG3F5i8nIwumAq8lArbC22LOin5ygNgp7NFweIYgGnhElnERYiAvZGRTE2NEXGd3IFd3d01zuM-E5hpFonGDsX5LXDHLSNmBqPGHZmlMODb1z3nO5P1ktAAJzEmG31SBHIQHCPr1fFdGiNQwlVn3nygHAOXwsOgikp1HokvjzgEKBBOMAsNlUqiLYhiPwJsCAA */
  context: {
    selectedNodeId: defaultSelectedSearchNodeId,
    searchBreadcrumb: [],
    parsedHistory: {
      isOHC: false,
      isDental: false,
      callbackOpen: false,
      isFromApp: false,
      hasInsurance: false,
      noServiceSelect: false,
      pathname: '/',
    },
    selectedPractitionerId: undefined,
    selectedPractitioner: undefined,
    selectedStandardScheduleOption: undefined,
    selectedFlexibleScheduleOption: undefined,
    modalsResolved: false,
  },
  id: 'Modals',
  initial: 'Initial',

  on: {
    searchTargetChanged: [
      {
        target: '.PractitionerModal',
        actions: { type: 'searchTargetChanged' },
        guard: { type: 'searchTargetChangedToPractitioner' },
      },
      {
        actions: { type: 'searchTargetChanged' },
      },
    ],
  },

  states: {
    Initial: {
      on: {
        urlParsed: {
          target: 'HistoryRestored',
          actions: {
            type: 'setContextFromHistory',
          },
        },
      },
    },

    HistoryRestored: {
      always: [
        {
          target: 'AuthenticationModal',
          guard: {
            type: 'isOHCAndNotLoggedIn',
          },
        },
        {
          target: 'ResolvingModal',
        },
      ],
    },

    AuthenticationModal: {
      on: {
        login: {
          target: 'FetchingOMEvents',
          reenter: true,
        },

        closeModal: 'ExitingAuthentication',
      },
    },

    ResolvingModal: {
      exit: {
        type: 'setModalsResolved',
      },
      always: [
        {
          target: 'NoModal',
          guard: {
            type: 'shouldSkipInitialModals',
          },
        },
        {
          target: 'PractitionerModal',
          guard: {
            type: 'shouldDisplayPractitionerModal',
          },
        },
        {
          target: 'ServiceModal',
          guard: {
            type: 'shouldDisplayServiceModal',
          },
        },
        {
          target: 'NoModal',
          guard: {
            type: 'hasNodeSelectedWithNoChildren',
          },
        },
        {
          target: 'InsuranceModal',
          guard: {
            type: 'shouldDisplayInsuranceModal',
          },
        },
        {
          target: 'CallbackModal',
          guard: {
            type: 'shouldDisplayCallbackModal',
          },
        },
        {
          target: 'LandingModal',
          guard: {
            type: 'shouldDisplayLandingModal',
          },
        },
        {
          target: 'NoModal',
        },
      ],
    },

    LandingModal: {
      entry: () => {
        sessionStorage.setItem('landingModalShown', 'true')
      },
      on: {
        openCallbackModal: {
          target: 'CallbackModal',
        },

        closeModal: 'NoModal',
        openInsuranceModal: 'InsuranceModal',
      },
    },

    ServiceModal: {
      initial: 'Initial',
      on: {
        openCallbackModal: {
          target: 'CallbackModal',
        },
        openOMLightModal: {
          target: 'OMLightModal',
          actions: assign(({ event }) => ({ selectedOMCarePlanGuid: event.carePlanGuid })),
        },
        closeModal: {
          target: 'NoModal',
        },
      },
      states: {
        Initial: {
          invoke: {
            id: 'fetchNode',
            input: ({ context }) => ({
              lang: i18n.language as SupportedLanguage,
              nodeId: context.selectedNodeId,
              isOhc: ohcService.isOHC(),
            }),
            onDone: [
              {
                target: 'DefaultView',
                guard: {
                  type: 'fetchNodeNotFound',
                },
              },
              {
                target: 'DisplayNodeChildren',
                actions: {
                  type: 'setBreadcrumb',
                },
                guard: {
                  type: 'fetchedNodeHasChildren',
                },
              },
              {
                target: 'DefaultView',
              },
            ],
            src: 'fetchNode',
          },
        },
        DisplayNodeChildren: {
          on: {
            selectService: {
              target: 'ResolvingServiceSelection',
            },
            displayServicesDefaultView: {
              target: 'DefaultView',
              actions: {
                type: 'setEmptyBreadcrumb',
              },
            },
          },
        },
        DefaultView: {
          initial: 'FavoriteServices',
          on: {
            selectService: {
              target: 'ResolvingServiceSelection',
            },
          },
          states: {
            FavoriteServices: {
              on: {
                displayAllServices: {
                  target: 'AllServices',
                },
              },
            },
            AllServices: {
              on: {
                displayFavoriteServices: {
                  target: 'FavoriteServices',
                },
              },
            },
          },
        },
        ResolvingServiceSelection: {
          invoke: {
            id: 'resolveServiceSelection',
            input: ({ event }) => {
              assertEvent(event, 'selectService')
              return {
                service: event.value,
                lang: i18n.language as SupportedLanguage,
                isOHC: ohcService.isOHC(),
              }
            },
            onDone: [
              {
                target: 'DefaultView',
                actions: {
                  type: 'setEmptyBreadcrumb',
                },
                guard: {
                  type: 'selectedServiceIsUndefined',
                },
                reenter: true,
              },
              {
                target: 'DisplayNodeChildren',
                actions: {
                  type: 'setBreadcrumb',
                },
                guard: {
                  type: 'selectedServiceIsNodeWithChildren',
                },
                reenter: true,
              },
              {
                target: '#Modals.NoteModal',
                guard: {
                  type: 'selectedServiceHasNoteModal',
                },
              },
              {
                target: '#Modals.NoModal',
                actions: {
                  type: 'notifyPractitionerIdSelected',
                },
                guard: {
                  type: 'selectedServiceIsPractitioner',
                },
              },
              {
                target: '#Modals.NoModal',
                actions: [
                  {
                    type: 'setSelectedNode',
                  },
                  { type: 'notifyNodeSelected' },
                ],
              },
            ],
            src: 'resolveServiceSelection',
          },
        },
      },
    },

    PractitionerModal: {
      initial: 'Initial',

      states: {
        Initial: {
          invoke: {
            id: 'fetchPractitionerDetails',
            input: ({ context }) => ({
              lang: i18n.language as SupportedLanguage,
              practitionerId: context.selectedPractitionerId ?? 0,
              isOHC: ohcService.isOHC(),
            }),
            onDone: [
              {
                target: 'DisplayPractitionerOptions',
                actions: [
                  { type: 'setSelectedPractitioner' },
                  { type: 'autoselectPractitionerOption' },
                ],
                guard: {
                  type: 'practitionerHasOptions',
                },
              },
              {
                target: '#Modals.NoModal',
                actions: [
                  {
                    type: 'setSelectedPractitioner',
                  },
                  {
                    type: 'autoselectPractitionerOption',
                  },
                  {
                    type: 'notifyPractitionerSelected',
                  },
                ],
              },
            ],
            src: 'fetchPractitionerDetails',
          },
        },
        DisplayPractitionerOptions: {
          on: {
            selectPractitionerOption: {
              target: 'DisplayPractitionerOptions',
              actions: {
                type: 'setSelectedPractitionerOption',
              },
            },

            closeModal: {
              target: '#Modals.NoModal',
              reenter: true,
              guard: 'practitionerOptionSelectionFulfilled',
              actions: { type: 'notifyPractitionerSelected' },
            },
          },
        },
        LoadingPractitionerDetails: {
          invoke: {
            id: 'fetchPractitionerDetails',
            input: ({ context }) => ({
              lang: i18n.language as SupportedLanguage,
              practitionerId: context.selectedPractitionerId ?? 0,
              isOHC: ohcService.isOHC(),
            }),
            onDone: {
              actions: {
                type: 'setSelectedPractitioner',
              },
              target: 'DisplayPractitionerOptions',
            },
            src: 'fetchPractitionerDetails',
          },
        },
      },
    },

    NoModal: {
      on: {
        openPractitionerModal: {
          target: '#Modals.PractitionerModal.LoadingPractitionerDetails',
          guard: 'hasSelectedPractitioner',
        },

        openServiceModal: [
          {
            target: 'NoteModal',
            guard: 'noteModalReplacesServiceModal',
          },
          {
            target: 'ServiceModal',
          },
        ],

        openDateModal: {
          target: 'DateModal',
        },

        openFilterModal: {
          target: 'FilterModal',
        },

        openLocationModal: {
          target: 'LocationModal',
        },

        openAuthenticationModal: {
          target: 'AuthenticationModal',
        },

        openOMLightModal: {
          target: 'FetchingOMEvents',
          actions: assign(({ event }) => ({ selectedOMCarePlanGuid: event.carePlanGuid })),
        },

        openInsuranceModal: {
          target: 'InsuranceModal',
        },
      },
    },

    CallbackModal: {
      on: {
        closeModal: {
          target: 'NoModal',
        },
        searchCallbackAppointments: {
          target: 'ServiceModal',
        },
      },
    },

    NoteModal: {
      on: {
        discardNoteModal: {
          target: 'ServiceModal',
          actions: assign({
            selectedNoteModalId: undefined,
            replaceServiceModalWithNoteModal: false,
          }),
        },
        closeModal: {
          target: 'NoModal',
        },
      },
    },

    InsuranceModal: {
      on: {
        closeModal: 'NoModal',
      },
    },

    DateModal: {
      on: {
        closeModal: 'NoModal',
      },
    },

    FilterModal: {
      on: {
        closeModal: 'NoModal',
      },
    },

    LocationModal: {
      on: {
        closeModal: 'NoModal',
      },
    },

    FetchingOMEvents: {
      invoke: {
        id: 'fetchOMEvents',
        src: 'fetchOMEvents',
        onDone: {
          actions: 'setOMEventsToContext',
          target: 'OMLightModal',
        },
        onError: {
          actions: 'setEmptyOMEventsToContext',
          target: 'OMLightModal',
        },
      },
    },

    OMLightModal: {
      always: [{ target: 'ExitingAuthentication', guard: 'shouldSkipOMLightModal' }],
      on: {
        closeModal: 'ExitingAuthentication',
        selectCarePlanGuid: {
          target: 'OMLightModal',
          actions: assign(({ event }) => ({ selectedOMCarePlanGuid: event.carePlanGuid })),
        },
      },
    },

    ExitingAuthentication: {
      always: [{ target: 'ResolvingModal', guard: 'isModalsUnresolved' }, { target: 'NoModal' }],
    },
  },
})

export const searchMachine = setup({
  types: {
    context: {} as {
      selectedDate: Dayjs
      isUserSelectedDate: boolean
      isUserSelectedNode: boolean
      filterOptions: FilterOptions
      selectedSearchTarget: SearchTarget
      selectedPractitionerOption?: AppointmentDuration | GroupedFlexibleScheduleOption
      breadcrumb: SearchTarget[]
      selectedNodeLocation: string[]
      selectedPractitionerLocation: string[]
      userSelectedAppointmentTypes?: AppointmentType[]
      pushNextChangeToBreadcrumb?: boolean
    },
    events: {} as
      | { type: 'setSearchTarget'; target: SearchTarget; pushChangeToBreadcrumb?: boolean }
      | { type: 'setSearchFilters'; filters: Partial<FilterOptions> }
      | { type: 'setSearchLocation'; location: string[] }
      | { type: 'setSearchDate'; date: Dayjs }
      | {
          type: 'setSelectedPractitionerOption'
          value: AppointmentDuration | GroupedFlexibleScheduleOption | undefined
        }
      | { type: 'setPractitionerSearch'; value: SelectedPractitioner }
      | { type: 'setNodeSearch'; value: SelectedNode },
  },
  actions: {
    searchTargetChanged: enqueueActions(({ context, enqueue }) => {
      enqueue.assign(() => {
        const lastCrumb = context.breadcrumb.slice(-1)[0]
        if (!lastCrumb) {
          return { breadcrumb: [context.selectedSearchTarget], pushNextChangeToBreadcrumb: false }
        }
        if (lastCrumb.id === context.selectedSearchTarget.id) {
          return { pushNextChangeToBreadcrumb: false }
        }
        if (
          lastCrumb.value === SearchTargetValue.Node &&
          context.selectedSearchTarget.value === SearchTargetValue.Practitioner &&
          context.pushNextChangeToBreadcrumb
        ) {
          return {
            breadcrumb: [lastCrumb, context.selectedSearchTarget],
            pushNextChangeToBreadcrumb: false,
          }
        }
        return { breadcrumb: [context.selectedSearchTarget], pushNextChangeToBreadcrumb: false }
      })
      enqueue.sendParent({ type: 'searchTargetChanged', value: context.selectedSearchTarget })
    }),
    resetPractitionerLocations: assign({ selectedPractitionerLocation: [] }),
    setNodeSearch: assign(({ event }) => {
      assertEvent(event, 'setNodeSearch')
      return {
        selectedSearchTarget: {
          value: SearchTargetValue.Node,
          id: event.value.nodeId,
        } as SearchTarget,
      }
    }),
    setUserSelectedNode: assign(({ event }) => {
      assertEvent(event, 'setSearchTarget')
      return { selectedSearchTarget: event.target, isUserSelectedNode: true }
    }),
    setSelectedPractitioner: assign(({ event }) => {
      assertEvent(event, 'setSearchTarget')
      return {
        selectedSearchTarget: event.target,
        pushNextChangeToBreadcrumb: event.pushChangeToBreadcrumb,
      }
    }),
    setPractitionerSearch: assign(({ event }) => {
      assertEvent(event, 'setPractitionerSearch')
      return {
        selectedSearchTarget: {
          value: SearchTargetValue.Practitioner,
          id: event.value.practitionerId,
        } as SearchTarget,
        selectedPractitionerOption: event.value.option,
      }
    }),
    setSearchLocation: assign(({ event, context }) => {
      assertEvent(event, 'setSearchLocation')
      const location = event.location
      setDefaultSelectedLocation(location)

      const target =
        context.selectedSearchTarget.value === SearchTargetValue.Node
          ? 'selectedNodeLocation'
          : 'selectedPractitionerLocation'
      return {
        [target]: location,
        ...(equal(location, allLocationsSelection)
          ? {
              filterOptions: {
                ...context.filterOptions,
                appointmentTypes: [AppointmentType.Video, AppointmentType.Phone],
              },
            }
          : {}),
      }
    }),
    setSearchDate: assign(({ event }) => {
      assertEvent(event, 'setSearchDate')
      return {
        selectedDate: event.date,
        isUserSelectedDate: true,
      }
    }),
    setSearchFilters: assign(({ context, event }) => {
      assertEvent(event, 'setSearchFilters')
      return {
        filterOptions: { ...context.filterOptions, ...event.filters },
        userSelectedAppointmentTypes: event.filters.appointmentTypes,
      }
    }),
    setSelectedPractitionerOption: assign(({ event }) => {
      assertEvent(event, 'setSelectedPractitionerOption')
      return {
        selectedPractitionerOption: event.value,
      }
    }),
  },
  guards: {
    searchTargetIsPractitioner: ({ event }) => {
      assertEvent(event, 'setSearchTarget')
      return event.target.value === SearchTargetValue.Practitioner
    },
    searchTargetHasChanged: ({ event, context }) => {
      assertEvent(event, 'setSearchTarget')
      return event.target.id !== context.selectedSearchTarget.id
    },
    searchNodeChanged: ({ event, context }) => {
      assertEvent(event, 'setNodeSearch')
      return (
        context.selectedSearchTarget.value === SearchTargetValue.Practitioner ||
        context.selectedSearchTarget.id !== event.value.nodeId
      )
    },
    searchPractitionerChanged: ({ event, context }) => {
      assertEvent(event, 'setPractitionerSearch')
      return (
        context.selectedSearchTarget.value === SearchTargetValue.Node ||
        context.selectedSearchTarget.id !== event.value.practitionerId
      )
    },
    searchTargetIsPractitionerAndChanged: and([
      'searchTargetIsPractitioner',
      'searchTargetHasChanged',
    ]),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QGUwEMBOBjAFgYljABdVNcAxASwBsiwNYBtABgF1FQAHAe1kqMrcAdhxAAPRAFoAzAHYAnADoAjAFZmqgByqATPJ17pRgDQgAnoh3Lmio5vn6dAFk0A2J9NWuAvt9OlsfEISdECAGW4sNAFhFnYkEB4+GJEEiQQZTVlFex1ZV1cDdVc1V1MLBGl3W2UdZlktVQVmAt9-UNwCYgDcABFosDjRJP5BVNB0mVlpRXUHV1lnV2YneVVyy2ynV11Cks1mTR1pJx02kB6g4gBBTh5KISIAWzBHy4BZbghBtmHeUeEokmpyctlksmULlcVXyK1kGwQqlOij022c+QOqmk50uXSIAAUMGgsAIUvRLkMEiMUkDEMppHoVCC5MpZMw9AsEcp5KDuR4NE5DvJmPofH4Lh0rgSiSSAUJyZLGMp4lx-jS0ogGTpFIVFmpVFj6qplAilHVjk58o1ZE4tMocZK8QA5L5gCm-Klqsa0hDKRTMNSHGGaW3KdxNLk8lQ86QCoUi7Hi3HBF3fCnKv7Jb0ahDMBHMB1kHCKVNux3BS4AFUwMCIlNVWcBOYZNlkmiMdTc0hDewRVlcin0IsO2gWOhKifaRZLruT3Ul1YwtaVKsSXqbE01rk0KPk9iHmgOIstCOkax13Py45K220hcCM7T5fnRcXy50q+p2c3ufz99wiiEsSpJjAqRZ4lWNbEPWa6NuM4iIJiOpaF2rLyKy0gmuYdLsjU7ZnswwYNP+xZAbKZIYHOISvlBdYZp6cE+lkoJ7oKBo8lkDTSAiWJ+hx7Joi0RhilOD5kSBwhgYEEELrRjAfpmco+vIEKKAotpsoYKmrAiNrZHIOyxnkaxyJOErTuJcpSZ0FZgNQYAkpAlkUQA8pwKQwV+G4Ib625KBo7iHAY+TbE4CKCgOix6PSh6Cm2SK+OKQiuvACSXIp6o-pIyg5bYVSrMK3YuO2YXYRkWSzMwVX1Dyug6AamgkY+ZZFhl34+ZI0J5e4DiESGh4nOFGiDqsqwlC0JSsk1zmgZRkptd56S1Ie-r1c4RjqE4WEVGxI08vI40FDlsiJd4QA */
  context: {
    selectedSearchTarget: {
      id: defaultSelectedSearchNodeId,
      value: SearchTargetValue.Node,
    },
    selectedDate: defaultSelectedDate,
    isUserSelectedDate: false,
    isUserSelectedNode: false,
    filterOptions: defaultFilterOptions,
    selectedNodeLocation: getDefaultSelectedLocation(),
    selectedPractitionerLocation: [],
    breadcrumb: [],
  },
  id: 'Search',
  initial: 'NodeSearch',
  on: {
    setSearchFilters: {
      actions: {
        type: 'setSearchFilters',
      },
    },

    setSearchLocation: {
      actions: {
        type: 'setSearchLocation',
      },
    },

    setSearchDate: {
      actions: {
        type: 'setSearchDate',
      },
    },

    setPractitionerSearch: [
      {
        target: '.PractitionerSearch',
        actions: 'setPractitionerSearch',
        guard: { type: 'searchPractitionerChanged' },
        reenter: true,
      },
      {
        target: '.PractitionerSearch',
        actions: 'setPractitionerSearch',
        reenter: false,
      },
    ],

    setNodeSearch: [
      {
        target: '.NodeSearch',
        actions: 'setNodeSearch',
        guard: { type: 'searchNodeChanged' },
        reenter: true,
      },
      {
        target: '.NodeSearch',
        actions: 'setNodeSearch',
        reenter: false,
      },
    ],
  },
  states: {
    NodeSearch: {
      on: {
        setSearchTarget: [
          {
            target: 'PractitionerSearch',

            guard: {
              type: 'searchTargetIsPractitioner',
            },

            actions: {
              type: 'setSelectedPractitioner',
            },

            reenter: true,
          },
          {
            target: 'NodeSearch',
            actions: { type: 'setUserSelectedNode' },
            guard: { type: 'searchTargetHasChanged' },
            reenter: true,
          },
          { target: 'NodeSearch', actions: { type: 'setUserSelectedNode' }, reenter: false },
        ],
      },
      entry: {
        type: 'searchTargetChanged',
      },
    },

    PractitionerSearch: {
      on: {
        setSearchTarget: [
          {
            target: 'PractitionerSearch',

            guard: { type: 'searchTargetIsPractitionerAndChanged' },

            actions: {
              type: 'setSelectedPractitioner',
            },

            reenter: true,
          },
          {
            target: 'PractitionerSearch',

            guard: {
              type: 'searchTargetIsPractitioner',
            },

            actions: {
              type: 'setSelectedPractitioner',
            },

            reenter: false,
          },
          {
            target: 'NodeSearch',
            actions: 'setUserSelectedNode',
            reenter: true,
          },
        ],

        setSelectedPractitionerOption: {
          target: 'PractitionerSearch',
          actions: 'setSelectedPractitionerOption',
          reenter: false,
        },
      },
      entry: [
        {
          type: 'searchTargetChanged',
        },
        { type: 'resetPractitionerLocations' },
      ],
    },
  },
})

export const authMachine = setup({
  types: {
    // eslint-disable-next-line @typescript-eslint/ban-types
    context: {} as {},
    events: {} as { type: 'login' } | { type: 'logout' },
  },
  actions: {
    notifyLogin: function () {
      // TODO
    },
    notifyLogout: function () {
      // TODO
    },
    notifyAuthenticationExpired: function () {
      // TODO
    },
  },
  actors: {
    initializeAuthenticationState: fromPromise(async (): Promise<void> => {}),
    checkAuthenticationState: fromPromise(async (): Promise<void> => {}),
  },
  guards: {
    isAuthenticated: function () {
      // TODO
      return true
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QEECuAXAFmAduglgMYCGBA9jgHQCSO+BxANgMQQViX44BuZA1hy718TfAC8waLLgIlyOAMrpSYANoAGALqJQABzKxhFHSAAeiABzqAzJQBMATgCM6gCwWL7iwFYAbAHYAGhAAT0RrVydKB19XSP9rf0i7SNcAXzTgqWw8IlJ8Cho6BhY2HEEefgrhUQlsmTz5JRVVJ20kEH1DeRNzBABaa3VfSidrCxc7dRTvCwc-YLCECKiYuKcEpKcUpwysjBzZfML63LlIZkYyKDIMDXa9AyMcXvDvf0pXOyc4x29-pzbRaIWJRawOCz+dTTWLWIbpTIgU5HeSUZGNSBFGosUywZToDjEABmBIATgAKbzQgCUzHRcgKVHpKggWJK9xMXWerwQgLclHeiWsdisvkBvmBCF81hGMvmFnG0P8or2SIODQZJ3VZxZlAAwthCHwuFBmU18WBWOxOJUBJRCIa+GbGc0CRyOlyeh0+j8ppQAsr-r4HDK7ElJf5ZpRhj8HOoHK5-D8IqrnVrpDqCayDWAjSa04oLVbyjbeHaHbmndqUS6La0Hp0nl7QH0UrZ4XZwRDwdKHJLPN5o05POptk5nHELKnq41GZQAKo4Ygz84QS7XLjux7dRk8mVRSMJFIQ1w2ayS0-qaOuaV2XwigIbXwZRE4MgQOAmAucpu772IQZvFcUZxkmaZXFmeYJVCSxbH8KF3HWBN-DsbxdkRAs2RERgfx3Yx-wQTtgPUeDYnvVD5XDGDeSTewEJiH54JFMNpwzGt00ODEIFw7kCIsXxB34-4qUTEjHCCajvDDSh+ITMM5j+NxWM4zUmRXXVaGxHjmzMRBnFsIT-jcKEUIcCSlmlYDZL8JxgycND+OUjVjjUtiuP1R183U80VG0v8W0QX0LFGciUI8EVwUlMVBzlVwe2+KYxSczM50XZc3NXPz8ICgZhwcShwVHb5b18XwJm8SV-nyzwUMTWY5jMqcXyAA */
  context: {},
  id: 'Authentication',
  initial: 'Initial',
  states: {
    Initial: {
      invoke: {
        id: 'initializeAuthenticationState',
        input: {},
        onDone: [
          {
            target: 'Authenticated',
            guard: {
              type: 'isAuthenticated',
            },
          },
          {
            target: 'Unauthenticated',
          },
        ],
        src: 'initializeAuthenticationState',
      },
    },
    Authenticated: {
      initial: 'Initial',
      on: {
        logout: {
          target: 'Unauthenticated',
          actions: {
            type: 'notifyLogout',
          },
        },
      },
      states: {
        Initial: {
          after: {
            '500': {
              target: 'CheckingAuthenticationState',
            },
          },
        },
        CheckingAuthenticationState: {
          invoke: {
            id: 'checkAuthenticationState',
            input: {},
            onDone: [
              {
                target: 'Initial',
                guard: {
                  type: 'isAuthenticated',
                },
              },
              {
                target: '#Authentication.Unauthenticated',
                actions: {
                  type: 'notifyAuthenticationExpired',
                },
              },
            ],
            src: 'checkAuthenticationState',
          },
        },
      },
    },
    Unauthenticated: {
      on: {
        login: {
          target: 'Authenticated',
          actions: {
            type: 'notifyLogin',
          },
        },
      },
    },
  },
})

export const appMachine = setup({
  types: {
    context: {} as {
      urlMachineRef: ActorRefFrom<typeof urlMachine>
      modalMachineRef: ActorRefFrom<typeof modalMachine>
      searchMachineRef: ActorRefFrom<typeof searchMachine>
      // TODO: authMachineRef: ActorRefFrom<typeof authMachine>
    },
    events: {} as
      | { type: 'searchTargetChanged'; value: SearchTarget }
      | { type: 'nodeSelected'; value: { nodeId: string } }
      | {
          type: 'practitionerSelected'
          value: {
            practitionerId: number
            option: AppointmentDuration | GroupedFlexibleScheduleOption | undefined
          }
        }
      | { type: 'urlParsed'; value: ParsedURL },
  },
}).createMachine({
  id: 'AppMachine',
  devTools: true,
  context: ({ spawn }) => ({
    urlMachineRef: spawn(urlMachine),
    modalMachineRef: spawn(modalMachine),
    searchMachineRef: spawn(searchMachine),
    // TODO: authMachineRef: spawn(authMachine),
  }),
  on: {
    urlParsed: {
      actions: ({ context, event }) => {
        context.modalMachineRef.send(event)
        if (event.value.practitionerId) {
          context.searchMachineRef.send({
            type: 'setPractitionerSearch',
            value: { practitionerId: event.value.practitionerId },
          })
        } else if (event.value.nodeId) {
          context.searchMachineRef.send({
            type: 'setNodeSearch',
            value: { nodeId: event.value.nodeId },
          })
        }
        if (event.value.date) {
          context.searchMachineRef.send({ type: 'setSearchDate', date: event.value.date })
        }
        if (event.value.filterOptions) {
          context.searchMachineRef.send({
            type: 'setSearchFilters',
            filters: event.value.filterOptions,
          })
        }
        if (event.value.location) {
          context.searchMachineRef.send({
            type: 'setSearchLocation',
            location: event.value.location,
          })
        }
      },
    },
    searchTargetChanged: {
      actions: ({ context, event }) => {
        context.modalMachineRef.send({ type: 'searchTargetChanged', value: event.value })
      },
    },
    nodeSelected: {
      actions: ({ context, event }) => {
        context.searchMachineRef.send({
          type: 'setSearchTarget',
          target: { value: SearchTargetValue.Node, id: event.value.nodeId },
        })
      },
    },
    practitionerSelected: {
      actions: ({ context, event }) => {
        context.searchMachineRef.send({
          type: 'setSearchTarget',
          target: {
            value: SearchTargetValue.Practitioner,
            id: event.value.practitionerId,
          },
        })
        if (event.value.option) {
          context.searchMachineRef.send({
            type: 'setSelectedPractitionerOption',
            value: event.value.option,
          })
        }
      },
    },
  },
})
